import React, { Component } from 'react';
import { connect } from 'react-redux';
import { IWithGoogleReCaptchaProps, withGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Button } from 'antd';

import { withSignUp } from './withSignUp';
import { SignUpDivider } from './SignUpDivider';
import { CognitoAction } from '../../../services/cognito';
import { SocialLoginButtons } from '../login/socialLoginButtons';
import { RootState } from '../../../slices/rootReducer';
import { selectResendSignByEmail, selectSignUpByEmail } from '../../../slices/signup/signup.selectors';
import { ISignUpByEmailRequest, resendSignUpEmail } from '../../../slices/signup/signup.thunks';
import { RecaptchaActions, SignUpTier } from './types';
import ReCAPTCHA from 'react-google-recaptcha';
import { RECAPTCHA_FALLBACK_SITE_KEY } from '../../../config';
import { showErrorMessage } from '../../../slices/notifications';
import { RECAPTCHA_ERROR } from './constants';

import styles from './SignUpEmailLinkSent.module.scss';

interface IDispatchProps {
  resendSignUpEmail: (values: ISignUpByEmailRequest) => void;
  showErrorMessage: (message: string) => void;
}

interface IMapStateToPros {
  selectedEmail?: string;
  showReCaptchaV2: boolean;
}

interface IComponentProps {
  tier: SignUpTier;
}

type Props = IComponentProps & IMapStateToPros & IDispatchProps & Partial<IWithGoogleReCaptchaProps>;

class SignUpEmailLinkSentComponent extends Component<Props> {
  constructor(props: Props) {
    super(props);

    this.handleResendEmail = this.handleResendEmail.bind(this);
    this.handleRecaptchaV2Change = this.handleRecaptchaV2Change.bind(this);
  }

  private handleRecaptchaV2Change(recaptchaToken: string | null) {
    const { selectedEmail: email, tier } = this.props;
    if (recaptchaToken === null || !email) {
      return;
    }
    this.props.resendSignUpEmail({
      email,
      recaptchaToken,
      tier,
      useFallback: true
    });
  }

  private async handleResendEmail() {
    const { selectedEmail: email, tier, resendSignUpEmail, showErrorMessage } = this.props;
    if (!email) {
      return;
    }
    const recaptchaToken = await this.executeRecaptcha(RecaptchaActions.ResendSignUpEmail);
    if (!recaptchaToken) {
      showErrorMessage(RECAPTCHA_ERROR);
      return;
    }
    resendSignUpEmail({
      email,
      recaptchaToken,
      tier,
      useFallback: false
    });
  }

  private async executeRecaptcha(action: string): Promise<string | null> {
    const { googleReCaptchaProps: { executeRecaptcha } = {} } = this.props;

    if (!executeRecaptcha) {
      return null;
    }

    return executeRecaptcha(action);
  }

  private renderText() {
    const { selectedEmail } = this.props;
    return (
      <div className={styles.text}>
        <p>A link has been sent to your email
          <div className={styles.email}>
            {selectedEmail}
          </div>
        </p>
        <p className={styles.shortParagraph}>Please check your email and click on the link to proceed</p>
        {this.renderResendEmail()}
      </div>
    );
  }

  private renderRecaptchaV2() {
    if (!this.props.showReCaptchaV2) {
      return null;
    }

    return (
      <div className={styles.recaptchav2}>
        <ReCAPTCHA
          sitekey={RECAPTCHA_FALLBACK_SITE_KEY}
          onChange={this.handleRecaptchaV2Change}
        />
      </div>
    );
  }

  private renderResendEmail() {
    return (
      <Button type='link' className={styles.resendEmail} onClick={this.handleResendEmail}>
        Resend email
      </Button>
    );
  }

  private renderOrDivider() {
    return (
      <SignUpDivider />
    );
  }

  private renderSocialButtons() {
    return <div className={styles.socialButtons}>
      <SocialLoginButtons action={CognitoAction.SignUp} />
    </div>;
  }

  render(): JSX.Element {
    return (
      <div className={styles.content}>
        {this.renderText()}
        {this.renderRecaptchaV2()}
        {this.renderOrDivider()}
        {this.renderSocialButtons()}
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => {
  const { email: selectedEmail } = selectSignUpByEmail(state);
  const { showReCaptchaV2 } = selectResendSignByEmail(state);

  return {
    selectedEmail,
    showReCaptchaV2
  };
};

const mapDispatchToProps: IDispatchProps = {
  resendSignUpEmail,
  showErrorMessage
};

const SignUpEmailLinkSentWithRecaptcha = withGoogleReCaptcha(SignUpEmailLinkSentComponent);
const SignUpEmailLinkSentWithRecaptchaAndWithSignUp = withSignUp(SignUpEmailLinkSentWithRecaptcha);

export const SignUpEmailLinkSent = connect(mapStateToProps, mapDispatchToProps)(SignUpEmailLinkSentWithRecaptchaAndWithSignUp);
